import { useMemo, useState } from "react"

import { useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { updateModelsStatusBatch } from "@/api/admin-api"
import { Brain, Gauge, Plus, Shield, ShieldCheck } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { Switch } from "@/components/ui/switch"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"

import { AccessPolicyDialog } from "./access-policy-dialog"
import { AddModelDialog } from "./add-model-dialog"
import { CollapsibleManagementSection } from "./collapsible-management-section"
import { DelegationModeControl } from "./delegation-mode-control"
import { DeleteModelDialog, type ModelToDelete } from "./delete-model-dialog"
import { EditModelDialog } from "./edit-model-dialog"
import { EffortAccessDialog } from "./effort-access-dialog"
import { isRowConfigured } from "./effort-access-utils"
import { ModelsTable, type ModelsTableRow } from "./models-table"
import {
  useAdminModelAccessPolicyQuery,
  useAdminModelBaseAccessPolicyQuery,
  useAdminModelBaseEffortAccessQuery,
  useAdminModelEffortAccessQuery,
  useClearModelAccessPolicyMutation,
  useCreateModelMutation,
  useDeleteModelMutation,
  useUpdateModelAccessPolicyMutation,
  useUpdateModelBaseAccessPolicyMutation,
  useUpdateModelBaseEffortAccessMutation,
  useUpdateModelEffortAccessMutation,
  useUpdateModelStatusMutation,
} from "../hooks/admin-queries"
import { useModelsTab } from "../hooks/use-models-tab"
import { isPolicyConfigured } from "../utils/access-policy"

export function ModelsManagementCard() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [updatingModelId, setUpdatingModelId] = useState<string | undefined>()
  const [isUpdatingAll, setIsUpdatingAll] = useState(false)
  const [isAccessDialogOpen, setIsAccessDialogOpen] = useState(false)
  const [accessModelId, setAccessModelId] = useState<string | undefined>()
  const [isBaseAccessDialogOpen, setIsBaseAccessDialogOpen] = useState(false)
  const [isBaseEffortAccessDialogOpen, setIsBaseEffortAccessDialogOpen] = useState(false)
  const [isEffortAccessDialogOpen, setIsEffortAccessDialogOpen] = useState(false)
  const [effortAccessModelId, setEffortAccessModelId] = useState<string | undefined>()
  const [isAddModelDialogOpen, setIsAddModelDialogOpen] = useState(false)
  const [isDeleteModelDialogOpen, setIsDeleteModelDialogOpen] = useState(false)
  const [deletingModel, setDeletingModel] = useState<ModelToDelete | null>(null)
  const [editingModelId, setEditingModelId] = useState<string | null>(null)

  const {
    activeTab,
    setTab,
    rows: activeRows,
    allRows,
    isLoading: isLoadingActive,
    queryKey: activeQueryKey,
  } = useModelsTab()

  const createModelMutation = useCreateModelMutation()
  const deleteModelMutation = useDeleteModelMutation()
  const updateMutation = useUpdateModelStatusMutation()
  const updateAccessMutation = useUpdateModelAccessPolicyMutation()
  const clearAccessMutation = useClearModelAccessPolicyMutation()
  const {
    data: baseAccessPolicy,
    isLoading: isLoadingBaseAccessPolicy,
    isError: isBaseAccessPolicyError,
  } = useAdminModelBaseAccessPolicyQuery(true)
  const updateBaseAccessMutation = useUpdateModelBaseAccessPolicyMutation()
  const {
    data: modelAccessPolicy,
    isLoading: isLoadingAccessPolicy,
    isError: isAccessPolicyError,
  } = useAdminModelAccessPolicyQuery(accessModelId, isAccessDialogOpen)
  const {
    data: baseEffortAccess,
    isLoading: isLoadingBaseEffortAccess,
    isError: isBaseEffortAccessError,
  } = useAdminModelBaseEffortAccessQuery(true)
  const updateBaseEffortAccessMutation = useUpdateModelBaseEffortAccessMutation()
  const {
    data: modelEffortAccess,
    isLoading: isLoadingModelEffortAccess,
    isError: isModelEffortAccessError,
  } = useAdminModelEffortAccessQuery(effortAccessModelId, isEffortAccessDialogOpen)
  const updateModelEffortAccessMutation = useUpdateModelEffortAccessMutation()

  const handleToggleStatus = async (modelId: string, enabled: boolean) => {
    setUpdatingModelId(modelId)
    try {
      await updateMutation.mutateAsync({ modelId, enabled, queryKey: activeQueryKey })
    } catch {
      // Error handled by React Query mutation
    } finally {
      setUpdatingModelId(undefined)
    }
  }

  const handleToggleAll = async (enabled: boolean) => {
    if (activeRows.length === 0) return

    setIsUpdatingAll(true)

    // Cancel any pending queries to prevent them from overwriting our optimistic update
    await queryClient.cancelQueries({ queryKey: activeQueryKey })

    // Get fresh data from cache instead of relying on potentially stale prop
    const currentModels = queryClient.getQueryData<ModelsTableRow[]>(activeQueryKey) || activeRows

    // Optimistically update all models at once
    const previousModels = currentModels
    const modelsToUpdate = currentModels.filter((model) => model.enabled !== enabled)

    // Update UI immediately
    queryClient.setQueryData(
      activeQueryKey,
      currentModels.map((model) => ({ ...model, enabled }))
    )

    try {
      // Use batch API to update all models in a single operation (avoids Cosmos DB race conditions)
      const updates = Object.fromEntries(modelsToUpdate.map((model) => [model.id, enabled]))
      await updateModelsStatusBatch(updates)
    } catch {
      // Error handled by React Query mutation
      // Rollback optimistic update on error
      queryClient.setQueryData(activeQueryKey, previousModels)
      // Refetch on error to ensure consistency
      await queryClient.invalidateQueries({ queryKey: activeQueryKey })
    } finally {
      setIsUpdatingAll(false)
    }
  }

  // Determine toggle state: true if all enabled, false if all disabled, undefined if mixed
  const allEnabledState = useMemo(() => {
    if (activeRows.length === 0) return false
    const enabledCount = activeRows.filter((model) => model.enabled).length
    if (enabledCount === 0) return false
    if (enabledCount === activeRows.length) return true
    return undefined // Mixed state
  }, [activeRows])

  const selectedModel = allRows.find((m) => m.id === accessModelId) as
    | (ModelsTableRow & { access_overridden?: boolean })
    | undefined
  const inheritByDefault = selectedModel?.access_overridden !== true

  const baseAccessConfigured = useMemo(() => {
    return isPolicyConfigured(baseAccessPolicy)
  }, [baseAccessPolicy])

  const baseEffortAccessConfigured = useMemo(() => {
    return Object.values(baseEffortAccess ?? {}).some(isRowConfigured)
  }, [baseEffortAccess])

  const effortAccessModel = allRows.find((m) => m.id === effortAccessModelId)

  function emptyDescriptionFor(tab: typeof activeTab): string | undefined {
    if (tab === "embedding") {
      return t("adminDashboard.models.emptyEmbeddings", {
        defaultValue: "No embedding models configured",
      })
    }
    if (tab === "specialized") {
      return t("adminDashboard.models.emptySpecialized", {
        defaultValue: "No specialized deployments configured",
      })
    }
    return undefined
  }

  const handleAddModel = async (data: Parameters<typeof createModelMutation.mutateAsync>[0]) => {
    try {
      await createModelMutation.mutateAsync(data)
      toast.success(t("adminDashboard.models.addDialog.success", "Model created successfully"))
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t("adminDashboard.models.addDialog.error", "Failed to create model")
      )
      throw error // Re-throw to keep dialog open on error
    }
  }

  const handleDeleteModel = async () => {
    if (!deletingModel) return

    try {
      await deleteModelMutation.mutateAsync(deletingModel.id)
      toast.success(t("adminDashboard.models.deleteDialog.success", "Model deleted successfully"), {
        description: deletingModel.displayName,
      })
      setIsDeleteModelDialogOpen(false)
      setDeletingModel(null)
    } catch (error) {
      toast.error(t("adminDashboard.models.deleteDialog.error", "Failed to delete model"), {
        description: error instanceof Error ? error.message : String(error),
      })
    }
  }

  return (
    <>
      <CollapsibleManagementSection
        icon={<Brain className="size-5" />}
        id="models"
        storageKey="admin-management-models-expanded"
        title={t("adminDashboard.models.title", "Model Management")}
        headerActions={
          <div className="flex max-w-full flex-wrap items-center gap-2 sm:gap-4">
            <Button
              className="flex items-center gap-2 px-3"
              disabled={isLoadingActive || isUpdatingAll}
              size="sm"
              type="button"
              variant="default"
              onClick={() => setIsAddModelDialogOpen(true)}
            >
              <Plus className="size-4" />
              {t("adminDashboard.models.addModel", "Add Model")}
            </Button>
            <Button
              className="flex items-center gap-2 px-3"
              disabled={isLoadingActive || isUpdatingAll}
              size="sm"
              type="button"
              variant="outline"
              onClick={() => setIsBaseAccessDialogOpen(true)}
            >
              {baseAccessConfigured ? (
                <ShieldCheck className="text-success size-4" />
              ) : (
                <Shield className="size-4" />
              )}
              {t("adminDashboard.models.baseAccess", { defaultValue: "Access for all" })}
            </Button>
            <Button
              className="flex items-center gap-2 px-3"
              disabled={isLoadingActive || isUpdatingAll}
              size="sm"
              type="button"
              variant="outline"
              onClick={() => setIsBaseEffortAccessDialogOpen(true)}
            >
              <Gauge className={baseEffortAccessConfigured ? "text-success size-4" : "size-4"} />
              {t("adminDashboard.models.effortAccess", { defaultValue: "Effort access" })}
            </Button>
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground text-sm">
                {t("adminDashboard.models.enableAll", "Enable All")}
              </span>
              <Switch
                aria-label={t("adminDashboard.models.enableAll", "Enable All")}
                checked={allEnabledState === true}
                disabled={isLoadingActive || isUpdatingAll || activeRows.length === 0}
                onCheckedChange={handleToggleAll}
              />
            </div>
            <DelegationModeControl capability="models" />
          </div>
        }
      >
        <Tabs value={activeTab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="chat">
              {t("adminDashboard.models.tabs.chat", { defaultValue: "Chat" })}
            </TabsTrigger>
            <TabsTrigger value="embedding">
              {t("adminDashboard.models.tabs.embeddings", { defaultValue: "Embeddings" })}
            </TabsTrigger>
            <TabsTrigger value="specialized">
              {t("adminDashboard.models.tabs.specialized", { defaultValue: "Specialized" })}
            </TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab}>
            {isLoadingActive ? (
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="flex items-center justify-between rounded-lg border p-4">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-6 w-12" />
                  </div>
                ))}
              </div>
            ) : (
              <ModelsTable
                disableAll={isUpdatingAll}
                emptyDescription={emptyDescriptionFor(activeTab)}
                isUpdating={updatingModelId}
                kind={activeTab}
                models={activeRows}
                onEdit={(modelId) => setEditingModelId(modelId)}
                onToggleStatus={handleToggleStatus}
                onDelete={(model) => {
                  setDeletingModel(model)
                  setIsDeleteModelDialogOpen(true)
                }}
                onEditAccess={(modelId) => {
                  setAccessModelId(modelId)
                  setIsAccessDialogOpen(true)
                }}
                onEditEffortAccess={
                  activeTab === "chat"
                    ? (modelId) => {
                        setEffortAccessModelId(modelId)
                        setIsEffortAccessDialogOpen(true)
                      }
                    : undefined
                }
              />
            )}
          </TabsContent>
        </Tabs>
      </CollapsibleManagementSection>

      <EditModelDialog
        isOpen={editingModelId !== null}
        modelId={editingModelId}
        onOpenChange={(open) => {
          if (!open) setEditingModelId(null)
        }}
      />

      <AccessPolicyDialog
        isLoading={isLoadingBaseAccessPolicy}
        isSaving={updateBaseAccessMutation.isPending}
        loadError={isBaseAccessPolicyError}
        open={isBaseAccessDialogOpen}
        policy={baseAccessPolicy}
        description={t("adminDashboard.models.baseAccessDescription", {
          defaultValue: "Default access policy applied to all models unless overridden per model.",
        })}
        title={t("adminDashboard.models.baseAccessTitle", {
          defaultValue: "Base access policy — Models",
        })}
        onOpenChange={setIsBaseAccessDialogOpen}
        onSave={async (policy) => {
          try {
            await updateBaseAccessMutation.mutateAsync({ policy })
            toast.success(
              t("adminDashboard.access.saveSuccess", { defaultValue: "Access policy updated" })
            )
          } catch (error) {
            toast.error(
              t("adminDashboard.access.saveError", {
                defaultValue: "Failed to update access policy",
              })
            )
            throw error
          }
        }}
      />

      <AccessPolicyDialog
        isLoading={isLoadingAccessPolicy}
        isSaving={updateAccessMutation.isPending || clearAccessMutation.isPending}
        loadError={isAccessPolicyError || isBaseAccessPolicyError}
        open={isAccessDialogOpen}
        policy={modelAccessPolicy}
        description={t("adminDashboard.access.dialogDescription", {
          defaultValue: "Configure who can access this resource.",
        })}
        inherit={{
          enabled: true,
          inheritByDefault,
          policy: baseAccessPolicy,
          label: t("adminDashboard.models.inheritBaseAccess", {
            defaultValue: "Inherit access for all",
          }),
          description: t("adminDashboard.models.inheritBaseAccessDescription", {
            defaultValue:
              "When enabled, this model uses the section baseline access policy. Disable to override for this model.",
          }),
          onSaveInherited: async () => {
            if (!accessModelId) return
            await clearAccessMutation.mutateAsync({ modelId: accessModelId })
            void queryClient.invalidateQueries({ queryKey: ["admin", "models"] })
          },
        }}
        title={t("adminDashboard.access.dialogTitle", {
          defaultValue: "Access policy — {{name}}",
          name: selectedModel?.displayName ?? accessModelId ?? "",
        })}
        onOpenChange={(open) => {
          setIsAccessDialogOpen(open)
          if (!open) {
            setAccessModelId(undefined)
          }
        }}
        onSave={async (policy) => {
          if (!accessModelId) return
          try {
            await updateAccessMutation.mutateAsync({ modelId: accessModelId, policy })
            toast.success(
              t("adminDashboard.access.saveSuccess", { defaultValue: "Access policy updated" })
            )
          } catch (error) {
            toast.error(
              t("adminDashboard.access.saveError", {
                defaultValue: "Failed to update access policy",
              })
            )
            throw error
          }
        }}
      />

      <EffortAccessDialog
        effortAccess={baseEffortAccess}
        isLoading={isLoadingBaseEffortAccess}
        isSaving={updateBaseEffortAccessMutation.isPending}
        loadError={isBaseEffortAccessError}
        open={isBaseEffortAccessDialogOpen}
        description={t("adminDashboard.models.baseEffortAccessDescription", {
          defaultValue:
            "Restrict individual reasoning effort levels for all models unless overridden per model.",
        })}
        title={t("adminDashboard.models.baseEffortAccessTitle", {
          defaultValue: "Effort access — All models",
        })}
        onOpenChange={setIsBaseEffortAccessDialogOpen}
        onSave={async (map) => {
          try {
            await updateBaseEffortAccessMutation.mutateAsync({ map })
            toast.success(
              t("adminDashboard.access.saveSuccess", { defaultValue: "Access policy updated" })
            )
          } catch (error) {
            toast.error(
              t("adminDashboard.access.saveError", {
                defaultValue: "Failed to update access policy",
              })
            )
            throw error
          }
        }}
      />

      <EffortAccessDialog
        effortAccess={modelEffortAccess}
        inheritMap={baseEffortAccess}
        isLoading={isLoadingModelEffortAccess}
        isSaving={updateModelEffortAccessMutation.isPending}
        loadError={isModelEffortAccessError || isBaseEffortAccessError}
        open={isEffortAccessDialogOpen}
        description={t("adminDashboard.models.effortAccessDescription", {
          defaultValue:
            "Restrict individual reasoning effort levels for this model. Levels without their own rule follow the baseline.",
        })}
        title={t("adminDashboard.models.effortAccessTitle", {
          defaultValue: "Effort access — {{name}}",
          name: effortAccessModel?.displayName ?? effortAccessModelId ?? "",
        })}
        onOpenChange={(open) => {
          setIsEffortAccessDialogOpen(open)
          if (!open) {
            setEffortAccessModelId(undefined)
          }
        }}
        onSave={async (map) => {
          if (!effortAccessModelId) return
          try {
            await updateModelEffortAccessMutation.mutateAsync({
              modelId: effortAccessModelId,
              map,
            })
            toast.success(
              t("adminDashboard.access.saveSuccess", { defaultValue: "Access policy updated" })
            )
          } catch (error) {
            toast.error(
              t("adminDashboard.access.saveError", {
                defaultValue: "Failed to update access policy",
              })
            )
            throw error
          }
        }}
      />

      <AddModelDialog
        existingModels={allRows.map((m) => ({ id: m.id, service_name: m.service_name }))}
        isOpen={isAddModelDialogOpen}
        isPending={createModelMutation.isPending}
        onOpenChange={setIsAddModelDialogOpen}
        onSubmit={handleAddModel}
      />

      <DeleteModelDialog
        isOpen={isDeleteModelDialogOpen}
        isPending={deleteModelMutation.isPending}
        model={deletingModel}
        onConfirm={handleDeleteModel}
        onOpenChange={setIsDeleteModelDialogOpen}
      />
    </>
  )
}
